import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../services/api';

const STATUS_BADGE = { Pending:'badge-muted', Partial:'badge-warn', Paid:'badge-green', Cancelled:'badge-danger' };

function printPayments(project) {
  const win = window.open('', '_blank');
  const rows = (project.payments||[]).map((p,i)=>`
    <tr>
      <td style="text-align:center">${i+1}</td>
      <td>${p.payment_date}</td>
      <td style="font-weight:700;color:#166534">KWD ${parseFloat(p.amount).toFixed(3)}</td>
      <td>${p.notes||'--'}</td>
    </tr>
  `).join('');
  const totalPaid = (project.payments||[]).reduce((s,p)=>s+parseFloat(p.amount||0),0);
  win.document.write(`<!DOCTYPE html><html><head><title>Payment History - ${project.project_name}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:30px;color:#111}
    .header{display:flex;justify-content:space-between;border-bottom:2px solid #00b368;padding-bottom:16px;margin-bottom:20px}
    .company{font-size:22px;font-weight:700;color:#00b368}
    .logo-img{height:40px;width:auto}
    .info-box{background:#f0fdf7;border:1px solid #86efac;border-radius:8px;padding:14px;margin-bottom:20px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px}
    .info-item label{font-size:10px;color:#888;display:block;margin-bottom:3px;text-transform:uppercase}
    .info-item span{font-size:14px;font-weight:600}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th{background:#f5f5f5;padding:10px 12px;text-align:left;font-size:11px;text-transform:uppercase;border-bottom:2px solid #eee;color:#666}
    td{padding:10px 12px;border-bottom:1px solid #f0f0f0}
    .total-row{font-weight:700;background:#f0fdf4}
    .footer{margin-top:30px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:12px}
  </style></head><body>
  <div class="header">
    <div><img src="https://teknulugy.online/logo.svg" class="logo-img" alt="Teknulugy"/><div style="font-size:12px;color:#666;margin-top:4px">IT Services - Kuwait</div></div>
    <div style="text-align:right"><div style="font-size:18px;font-weight:700">Payment History</div><div style="font-size:12px;color:#666">Generated: ${new Date().toLocaleDateString()}</div></div>
  </div>
  <div class="info-box">
    <div class="info-item"><label>Partner</label><span>${project.partner?.company_name||'--'}</span></div>
    <div class="info-item"><label>Project</label><span>${project.project_name}</span></div>
    <div class="info-item"><label>Commission</label><span style="color:#92400e">KWD ${parseFloat(project.commission_amount||0).toFixed(3)} (${parseFloat(project.commission_percentage||0).toFixed(1)}%)</span></div>
    <div class="info-item"><label>Total Paid</label><span style="color:#166534">KWD ${totalPaid.toFixed(3)}</span></div>
    <div class="info-item"><label>Remaining</label><span style="color:#dc2626">KWD ${parseFloat(project.remaining_amount||0).toFixed(3)}</span></div>
    <div class="info-item"><label>Status</label><span>${project.status}</span></div>
  </div>
  <table>
    <thead><tr><th>#</th><th>Date</th><th>Amount</th><th>Notes</th></tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr class="total-row"><td colspan="2">TOTAL PAID</td><td>KWD ${totalPaid.toFixed(3)}</td><td></td></tr></tfoot>
  </table>
  <div class="footer">Teknulugy IT Services - Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D - +965 55107000</div>
  </body></html>`);
  win.document.close();
  setTimeout(()=>win.print(),500);
}
const EMPTY_PARTNER = { company_name:'', representative:'', commission_percentage:'', business_licence:'', phone:'', email:'', notes:'' };
const EMPTY_PROJECT = { partner_id:'', project_name:'', total_amount:'', commission_percentage:'', project_date:'', notes:'' };

export default function PartnershipsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('projects');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [showPartnerForm, setShowPartnerForm] = useState(false);
  const [editPartner, setEditPartner]         = useState(null);
  const [partnerForm, setPartnerForm]         = useState(EMPTY_PARTNER);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectForm, setProjectForm]         = useState(EMPTY_PROJECT);
  const [payingProject, setPayingProject]     = useState(null);
  const [viewingPayments, setViewingPayments] = useState(null);
  const [payForm, setPayForm]                 = useState({ amount:'', payment_date:'', notes:'' });
  const setP = (k,v) => setPartnerForm(p=>({...p,[k]:v}));
  const setPr = (k,v) => setProjectForm(p=>({...p,[k]:v}));

  const { data:partnersData } = useQuery({ queryKey:['partners'], queryFn:()=>api.get('/partnerships/partners').then(r=>r.data) });
  const { data:projectsData, isLoading } = useQuery({
    queryKey:['partner-projects', partnerFilter],
    queryFn:()=>api.get('/partnerships/projects',{params:{partner_id:partnerFilter||undefined}}).then(r=>r.data),
  });

  const createPartnerMut = useMutation({
    mutationFn:()=>api.post('/partnerships/partners',partnerForm),
    onSuccess:()=>{ qc.invalidateQueries(['partners']); setShowPartnerForm(false); setPartnerForm(EMPTY_PARTNER); toast.success('Partner added!'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updatePartnerMut = useMutation({
    mutationFn:()=>api.put('/partnerships/partners/'+editPartner.id,partnerForm),
    onSuccess:()=>{ qc.invalidateQueries(['partners']); setEditPartner(null); toast.success('Updated!'); },
  });
  const createProjectMut = useMutation({
    mutationFn:()=>api.post('/partnerships/projects',projectForm),
    onSuccess:()=>{ qc.invalidateQueries(['partner-projects']); setShowProjectForm(false); setProjectForm(EMPTY_PROJECT); toast.success('Project added!'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const payMut = useMutation({
    mutationFn:({id,data})=>api.post('/partnerships/projects/'+id+'/pay',data),
    onSuccess:()=>{ qc.invalidateQueries(['partner-projects']); setPayingProject(null); setPayForm({amount:'',payment_date:'',notes:''}); toast.success('Payment recorded!'); },
    onError:e=>toast.error(e.response?.data?.message||'Error'),
  });
  const deleteProjectMut = useMutation({
    mutationFn:id=>api.delete('/partnerships/projects/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['partner-projects']); toast.success('Deleted'); },
  });

  const partners = partnersData?.data || [];
  const projects = projectsData?.data || [];

  const totalCommission = projects.reduce((s,p)=>s+parseFloat(p.commission_amount||0),0);
  const totalPaid       = projects.reduce((s,p)=>s+parseFloat(p.paid_amount||0),0);
  const totalRemaining  = projects.reduce((s,p)=>s+parseFloat(p.remaining_amount||0),0);

  const openEditPartner = p => { setEditPartner(p); setPartnerForm({company_name:p.company_name,representative:p.representative||'',commission_percentage:p.commission_percentage,business_licence:p.business_licence||'',phone:p.phone||'',email:p.email||'',notes:p.notes||''}); setShowPartnerForm(false); };

  const selectedPartner = partners.find(p=>String(p.id)===String(projectForm.partner_id));

  // Auto-fill commission when partner selected
  const handlePartnerSelect = (id) => {
    setPr('partner_id', id);
    const partner = partners.find(p=>String(p.id)===String(id));
    if (partner) setPr('commission_percentage', partner.commission_percentage);
  };

  // Live preview commission
  const previewTotal = parseFloat(projectForm.total_amount||0);
  const previewPct   = parseFloat(projectForm.commission_percentage||0);
  const previewComm  = parseFloat((previewTotal * previewPct / 100).toFixed(3));

  return (
    <div>
      {/* Stats */}
      <div className="g4" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total Partners</div><div className="stat-val">{partners.length}</div></div>
        <div className="card-sm"><div className="stat-label">Total Commission</div><div className="stat-val" style={{color:'var(--green)'}}>KWD {totalCommission.toFixed(3)}</div></div>
        <div className="card-sm"><div className="stat-label">Total Paid</div><div className="stat-val" style={{color:'var(--info)'}}>KWD {totalPaid.toFixed(3)}</div></div>
        <div className="card-sm"><div className="stat-label">Remaining</div><div className="stat-val" style={{color:'var(--warn)'}}>KWD {totalRemaining.toFixed(3)}</div></div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:16,alignItems:'center'}}>
        <div className="tab-bar">
          <button className={"tab-btn"+(tab==='projects'?" active":"")} onClick={()=>setTab('projects')}>Projects</button>
          <button className={"tab-btn"+(tab==='partners'?" active":"")} onClick={()=>setTab('partners')}>Partners</button>
        </div>
        {tab==='projects'&&(
          <>
            <select className="form-select" style={{width:200}} value={partnerFilter} onChange={e=>setPartnerFilter(e.target.value)}>
              <option value="">All Partners</option>
              {partners.map(p=><option key={p.id} value={p.id}>{p.company_name}</option>)}
            </select>
            {partnerFilter&&<button className="btn btn-outline btn-sm" onClick={()=>setPartnerFilter('')}>Clear</button>}
            <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>{setShowProjectForm(v=>!v);setProjectForm(EMPTY_PROJECT);}}>+ Add Project</button>
          </>
        )}
        {tab==='partners'&&(
          <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>{setShowPartnerForm(v=>!v);setEditPartner(null);setPartnerForm(EMPTY_PARTNER);}}>+ Add Partner</button>
        )}
      </div>

      {/* Pay Modal */}
      {payingProject&&(
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:16,padding:28,width:440,boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:4}}>Record Payment</div>
            <div style={{fontSize:13,color:'var(--muted)',marginBottom:16}}>{payingProject.project_name} — {payingProject.partner?.company_name}</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,background:'var(--surface2)',borderRadius:10,padding:14,marginBottom:16}}>
              <div><div style={{fontSize:11,color:'var(--muted)'}}>Commission</div><div style={{fontWeight:700,color:'var(--green)'}}>KWD {parseFloat(payingProject.commission_amount||0).toFixed(3)}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)'}}>Already Paid</div><div style={{fontWeight:700,color:'var(--info)'}}>KWD {parseFloat(payingProject.paid_amount||0).toFixed(3)}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)'}}>Remaining</div><div style={{fontWeight:700,color:'var(--warn)'}}>KWD {parseFloat(payingProject.remaining_amount||0).toFixed(3)}</div></div>
              <div><div style={{fontSize:11,color:'var(--muted)'}}>Status</div><div><span className={"badge "+(STATUS_BADGE[payingProject.status]||'badge-muted')}>{payingProject.status}</span></div></div>
            </div>
            <div className="form-group" style={{marginBottom:12}}>
              <label className="form-label">Amount to Pay (KWD) *</label>
              <input className="form-input" type="number" min="0" step="0.001" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))} placeholder="0.000"/>
            </div>
            <div className="form-group" style={{marginBottom:12}}>
              <label className="form-label">Payment Date *</label>
              <input className="form-input" type="date" value={payForm.payment_date} onChange={e=>setPayForm(p=>({...p,payment_date:e.target.value}))}/>
            </div>
            <div className="form-group" style={{marginBottom:20}}>
              <label className="form-label">Notes</label>
              <input className="form-input" value={payForm.notes} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))} placeholder="Payment notes..."/>
            </div>
            {/* Payment history */}
            {payingProject.payments && payingProject.payments.length>0 && (
              <div style={{marginBottom:16,borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--muted)',marginBottom:8}}>PAYMENT HISTORY</div>
                {payingProject.payments.map(pay=>(
                  <div key={pay.id} style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
                    <span style={{color:'var(--muted)'}}>{pay.payment_date}</span>
                    <span style={{fontWeight:600,color:'var(--green)'}}>KWD {parseFloat(pay.amount).toFixed(3)}</span>
                    <span style={{color:'var(--muted)',maxWidth:150,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pay.notes||'--'}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{display:'flex',gap:8}}>
              <button className="btn btn-primary" style={{flex:1,justifyContent:'center'}} onClick={()=>payMut.mutate({id:payingProject.id,data:payForm})}>Record Payment</button>
              <button className="btn btn-outline" onClick={()=>{setPayingProject(null);setPayForm({amount:'',payment_date:'',notes:''});}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {viewingPayments&&(
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:16,padding:28,width:600,maxHeight:'80vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <div>
                <div style={{fontSize:16,fontWeight:700}}>{viewingPayments.project_name}</div>
                <div style={{fontSize:12,color:'var(--muted)'}}>{viewingPayments.partner?.company_name}</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-outline btn-sm" style={{display:'flex',alignItems:'center',gap:4}} onClick={()=>printPayments(viewingPayments)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                  Print
                </button>
                <button className="btn btn-outline btn-sm" onClick={()=>setViewingPayments(null)}>Close</button>
              </div>
            </div>

            {/* Summary */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:16}}>
              <div style={{background:'#fff8f0',border:'1px solid #fed7aa',borderRadius:8,padding:12,textAlign:'center'}}>
                <div style={{fontSize:11,color:'var(--muted)',marginBottom:3}}>Commission</div>
                <div style={{fontWeight:700,color:'var(--warn)'}}>KWD {parseFloat(viewingPayments.commission_amount||0).toFixed(3)}</div>
              </div>
              <div style={{background:'#f0fdf7',border:'1px solid #86efac',borderRadius:8,padding:12,textAlign:'center'}}>
                <div style={{fontSize:11,color:'var(--muted)',marginBottom:3}}>Total Paid</div>
                <div style={{fontWeight:700,color:'var(--green)'}}>KWD {parseFloat(viewingPayments.paid_amount||0).toFixed(3)}</div>
              </div>
              <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:8,padding:12,textAlign:'center'}}>
                <div style={{fontSize:11,color:'var(--muted)',marginBottom:3}}>Remaining</div>
                <div style={{fontWeight:700,color:'var(--danger)'}}>KWD {parseFloat(viewingPayments.remaining_amount||0).toFixed(3)}</div>
              </div>
            </div>

            {/* Payments Table */}
            {viewingPayments.payments && viewingPayments.payments.length>0 ? (
              <table className="tbl">
                <thead><tr><th>#</th><th>Date</th><th>Amount</th><th>Notes</th></tr></thead>
                <tbody>
                  {viewingPayments.payments.map((p,i)=>(
                    <tr key={p.id}>
                      <td style={{color:'var(--muted)',fontSize:12}}>{i+1}</td>
                      <td style={{fontSize:12}}>{p.payment_date}</td>
                      <td style={{fontWeight:700,color:'var(--green)'}}>KWD {parseFloat(p.amount).toFixed(3)}</td>
                      <td style={{color:'var(--muted)',fontSize:12}}>{p.notes||'--'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{borderTop:'2px solid var(--border)'}}>
                    <td colSpan={2} style={{fontWeight:700,padding:'10px 14px'}}>TOTAL</td>
                    <td style={{fontWeight:700,color:'var(--green)',padding:'10px 14px'}}>KWD {(viewingPayments.payments||[]).reduce((s,p)=>s+parseFloat(p.amount||0),0).toFixed(3)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            ):(
              <div className="empty-state">No payments recorded yet</div>
            )}
          </div>
        </div>
      )}

      {/* PARTNERS TAB */}
      {tab==='partners'&&(
        <>
          {(showPartnerForm||editPartner)&&(
            <div className="card" style={{marginBottom:16}}>
              <div className="form-section-title">{editPartner?'Edit Partner':'New Partner'}</div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Company Name *</label>
                  <input className="form-input" value={partnerForm.company_name} onChange={e=>setP('company_name',e.target.value)} placeholder="Partner company name"/></div>
                <div className="form-group"><label className="form-label">Representative</label>
                  <input className="form-input" value={partnerForm.representative} onChange={e=>setP('representative',e.target.value)} placeholder="Contact person name"/></div>
                <div className="form-group"><label className="form-label">Commission %</label>
                  <input className="form-input" type="number" min="0" max="100" step="0.01" value={partnerForm.commission_percentage} onChange={e=>setP('commission_percentage',e.target.value)} placeholder="e.g. 10"/></div>
                <div className="form-group"><label className="form-label">Business Licence No</label>
                  <input className="form-input" value={partnerForm.business_licence} onChange={e=>setP('business_licence',e.target.value)} placeholder="Licence number"/></div>
                <div className="form-group"><label className="form-label">Phone</label>
                  <input className="form-input" value={partnerForm.phone} onChange={e=>setP('phone',e.target.value)} placeholder="+965 XXXX XXXX"/></div>
                <div className="form-group"><label className="form-label">Email</label>
                  <input className="form-input" type="email" value={partnerForm.email} onChange={e=>setP('email',e.target.value)} placeholder="partner@company.com"/></div>
                <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
                  <textarea className="form-textarea" value={partnerForm.notes} onChange={e=>setP('notes',e.target.value)} placeholder="Notes..."/></div>
              </div>
              <div style={{display:'flex',gap:8,marginTop:8}}>
                <button className="btn btn-primary" onClick={()=>editPartner?updatePartnerMut.mutate():createPartnerMut.mutate()}>Save Partner</button>
                <button className="btn btn-outline" onClick={()=>{setShowPartnerForm(false);setEditPartner(null);}}>Cancel</button>
              </div>
            </div>
          )}
          <div className="card">
            <table className="tbl">
              <thead><tr><th>Company</th><th>Representative</th><th>Commission %</th><th>Business Licence</th><th>Phone</th><th>Projects</th><th>Actions</th></tr></thead>
              <tbody>
                {partners.length===0&&<tr><td colSpan={7} className="empty-state">No partners added yet</td></tr>}
                {partners.map(p=>(
                  <tr key={p.id}>
                    <td style={{fontWeight:700}}>{p.company_name}</td>
                    <td style={{fontSize:12}}>{p.representative||'--'}</td>
                    <td><span className="badge badge-warn" style={{fontSize:13}}>{parseFloat(p.commission_percentage||0).toFixed(1)}%</span></td>
                    <td style={{fontFamily:'monospace',fontSize:12,color:'var(--muted)'}}>{p.business_licence||'--'}</td>
                    <td style={{color:'var(--muted)',fontSize:12}}>{p.phone||'--'}</td>
                    <td style={{textAlign:'center'}}><span className="badge badge-muted">{p.projects?.length||0}</span></td>
                    <td><div style={{display:'flex',gap:6}}>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEditPartner(p)}>Edit</button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* PROJECTS TAB */}
      {tab==='projects'&&(
        <>
          {showProjectForm&&(
            <div className="card" style={{marginBottom:16}}>
              <div className="form-section-title">New Partner Project</div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Partner *</label>
                  <select className="form-select" value={projectForm.partner_id} onChange={e=>handlePartnerSelect(e.target.value)}>
                    <option value="">Select partner...</option>
                    {partners.map(p=><option key={p.id} value={p.id}>{p.company_name}</option>)}
                  </select></div>
                <div className="form-group"><label className="form-label">Project Name *</label>
                  <input className="form-input" value={projectForm.project_name} onChange={e=>setPr('project_name',e.target.value)} placeholder="Project name"/></div>
                <div className="form-group"><label className="form-label">Total Amount (KWD) *</label>
                  <input className="form-input" type="number" min="0" step="0.001" value={projectForm.total_amount} onChange={e=>setPr('total_amount',e.target.value)} placeholder="0.000"/></div>
                <div className="form-group"><label className="form-label">Commission % (auto-filled)</label>
                  <input className="form-input" type="number" min="0" max="100" step="0.01" value={projectForm.commission_percentage} onChange={e=>setPr('commission_percentage',e.target.value)} placeholder="Auto from partner"/></div>
                <div className="form-group"><label className="form-label">Project Date</label>
                  <input className="form-input" type="date" value={projectForm.project_date} onChange={e=>setPr('project_date',e.target.value)}/></div>
                <div className="form-group"><label className="form-label">Notes</label>
                  <input className="form-input" value={projectForm.notes} onChange={e=>setPr('notes',e.target.value)} placeholder="Notes..."/></div>
              </div>

              {/* Live Preview */}
              {projectForm.partner_id && previewTotal>0 && (
                <div style={{background:'linear-gradient(135deg,#f0fdf7,#e8f8f1)',border:'1.5px solid #86efac',borderRadius:12,padding:16,margin:'16px 0'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--green)',marginBottom:12}}>COMMISSION PREVIEW</div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
                    <div style={{background:'#fff',borderRadius:8,padding:12,textAlign:'center'}}>
                      <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Project Total</div>
                      <div style={{fontSize:16,fontWeight:700}}>KWD {previewTotal.toFixed(3)}</div>
                    </div>
                    <div style={{background:'#fff',borderRadius:8,padding:12,textAlign:'center'}}>
                      <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Commission ({previewPct}%)</div>
                      <div style={{fontSize:16,fontWeight:700,color:'var(--warn)'}}>KWD {previewComm.toFixed(3)}</div>
                    </div>
                    <div style={{background:'var(--green)',borderRadius:8,padding:12,textAlign:'center'}}>
                      <div style={{fontSize:11,color:'rgba(255,255,255,0.8)',marginBottom:4}}>Partner: {selectedPartner?.company_name}</div>
                      <div style={{fontSize:16,fontWeight:700,color:'#fff'}}>KWD {previewComm.toFixed(3)}</div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{display:'flex',gap:8}}>
                <button className="btn btn-primary" onClick={()=>createProjectMut.mutate()}>Add Project</button>
                <button className="btn btn-outline" onClick={()=>{setShowProjectForm(false);setProjectForm(EMPTY_PROJECT);}}>Cancel</button>
              </div>
            </div>
          )}

          <div className="card">
            {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
              <>
                <table className="tbl">
                  <thead>
                    <tr><th>Partner</th><th>Project</th><th>Date</th><th>Total</th><th>Commission</th><th>Paid</th><th>Remaining</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {projects.length===0&&<tr><td colSpan={9} className="empty-state">No projects found</td></tr>}
                    {projects.map(p=>(
                      <tr key={p.id}>
                        <td>
                          <div style={{fontWeight:600}}>{p.partner?.company_name||'--'}</div>
                          <div style={{fontSize:11,color:'var(--muted)'}}>{p.partner?.representative||''}</div>
                        </td>
                        <td style={{fontWeight:500}}>{p.project_name}</td>
                        <td style={{color:'var(--muted)',fontSize:12}}>{p.project_date||'--'}</td>
                        <td style={{fontWeight:600}}>KWD {parseFloat(p.total_amount||0).toFixed(3)}</td>
                        <td style={{color:'var(--warn)',fontWeight:600}}>KWD {parseFloat(p.commission_amount||0).toFixed(3)} <span style={{fontSize:10,color:'var(--muted)'}}>({parseFloat(p.commission_percentage||0).toFixed(1)}%)</span></td>
                        <td style={{color:'var(--green)',fontWeight:600}}>KWD {parseFloat(p.paid_amount||0).toFixed(3)}</td>
                        <td>
                          <span style={{fontWeight:700,color:parseFloat(p.remaining_amount)>0?'var(--danger)':'var(--green)'}}>
                            KWD {parseFloat(p.remaining_amount||0).toFixed(3)}
                          </span>
                        </td>
                        <td><span className={"badge "+(STATUS_BADGE[p.status]||'badge-muted')}>{p.status}</span></td>
                        <td><div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          {p.status!=='Paid'&&p.status!=='Cancelled'&&(
                            <button className="btn btn-primary btn-sm" onClick={()=>setPayingProject(p)}>Pay</button>
                          )}
                          <button className="btn btn-outline btn-sm" style={{color:'var(--info)',borderColor:'var(--info)'}} onClick={()=>setViewingPayments(p)} title="View Payment History">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteProjectMut.mutate(p.id);}}>X</button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {projects.length>0&&(
                  <div style={{display:'flex',justifyContent:'flex-end',gap:24,padding:'16px 0 0',borderTop:'1px solid var(--border)',marginTop:8}}>
                    <div style={{fontSize:13}}><span style={{color:'var(--muted)'}}>Total Commission: </span><strong style={{color:'var(--warn)'}}>KWD {totalCommission.toFixed(3)}</strong></div>
                    <div style={{fontSize:13}}><span style={{color:'var(--muted)'}}>Total Paid: </span><strong style={{color:'var(--green)'}}>KWD {totalPaid.toFixed(3)}</strong></div>
                    <div style={{fontSize:13}}><span style={{color:'var(--muted)'}}>Remaining: </span><strong style={{color:'var(--danger)'}}>KWD {totalRemaining.toFixed(3)}</strong></div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
